import { Log } from "./MyHelpers";
import { ResourceIdentifier } from "./ResourceManager/ResourceIdentifier";
import { Resource } from "./ResourceManager/Resource";
import { TrackResource } from "./ResourceManager/TrackResource";
import { Howl } from "howler";
export type TrackLoadedCallback = () => void;
export type TrackLoadErrorCallback = () => void;


export type VolumeChangeCallback = (volumeLevel: number, volumeFactor: number) => void;
export type MaxVolumeFactorChangeChallback = (maxVolumeFactor: number) => void;

export interface ITrack
{
	Play(canPlayDuplicate?: boolean): void;
	PlayLoop(loopCount:number): void;
	PlayOrContinueLoop(loopCount:number): void;
	Stop(): void;
	Replay(): void;
	IsPlaying(): boolean;
	SetVolume(volumeFactor: number): void;
	GetVolume(): number;
	SetVolumeAnalog(volumeFactor: number): void;
	GetVolumeAnalog(): number;
	Load(): void;
	Duration(): number;
	Pause() : void

	AddLoadedCallback(callback: TrackLoadedCallback): void;
	AddLoadErrorCallback(callback: TrackLoadErrorCallback): void;

	Name: string;
	Path: string;
	Required: boolean;
}

export class Track implements ITrack
{
	private track: Howl = null;
	protected path: string = "";
	protected name: string = "";
	protected required: boolean;
	protected loop:boolean = false;
	protected loopCount:number = 0;

	protected volumeFactor: number = 1;
	protected volumePercent: number = 1;
	protected volume: number = 1;

	public static readonly LoopForEver:number = 0;

	//protected static readonly GlobalVolumeFactor = 1;

	constructor(path: string, name: string | ResourceIdentifier, required: boolean = true) {
		this.path = path;

		if (name instanceof ResourceIdentifier)
		{
			this.name = name.ID();
		}
		else
		{
			this.name = name;
		}

		this.required = required;

		if (path != "") {
			this.track = new Howl({
				src: path,
				volume: 0,	// default muted
				preload: false,
				onload: () => this.OnLoaded(),
				//onerror: () => this.OnLoadError(),
				onloaderror: () => this.OnLoadError(),
				onend: () => this.OnEnd(),
			});
		}

		if (this.track == null)
		{
			// Log.Debug("Track", "Track " + path + " is null");
		}
	}


	public Pause(): void {		
		this.loop = false;
		if (this.track == null) return;
		this.track.pause();
	}

	public Play(canPlayDuplicate?: boolean): void {
		if (this.track == null) return;
		
		if (this.IsPlaying() && !canPlayDuplicate)
		{
			//Log.Error(this.name, "Play() track is already playing!");
			this.Stop();
		}

		this.track.loop(false);
		this.track.play();

		// Log.Debug("Track", "Play() track " + this.Name);
	}

	public PlayLoop(loopCount:number = Track.LoopForEver): void {
		

		if (this.track == null) return;

		if (this.IsPlaying())
		{
			//Log.Error(this.name, "PlayLoop() track is already playing!");
			this.Stop();
		}

		this.loopCount = loopCount;
		this.loop = true;

		this.track.loop(true);
		this.track.play();

		// Log.Debug("Track", "PlayLoop() track " + this.Name);
	}

	public PlayOrContinueLoop(loopCount: number = Track.LoopForEver): void
	{
		if (this.track == null) return;

		if (this.IsPlaying())
		{
			// already playing, just continue
		}
		else
		{
			this.track.loop(true);
			this.track.play();
		}

		this.loopCount = loopCount;
		this.loop = true;

		// Log.Debug("Track", "PlayLoop() track " + this.Name);
	}

	public Stop(): void {
		this.loop = false;

		if (this.track == null) return;
		this.track.stop();

		// Log.Debug("Track", "Stop() track " + this.Name);
	}

	public Replay(): void {
		if (this.track == null) return;

		this.track.stop();
		this.track.play();

		// Log.Debug("Track", "Replay() track " + this.Name);
	}

	public IsPlaying(): boolean {
		if (this.track == null) return false;
		return this.track.playing();
	}

	public SetVolume(volumeFactor: number): void {
		//if (volumeFactor > 0) this.track.mute(false);
		//volumeFactor *= Track.GlobalVolumeFactor;
		//// Log.Debug("Track|" + this.name, "Volume set to " + volumeFactor);

		this.volumeFactor = volumeFactor;
		this.volume = this.volumeFactor * this.volumePercent;
		
		if (this.track == null) return;
		this.track.volume(this.volume);

		// console.log(`AudioManager.SetVolume() ${this.Name} factor ${this.volumeFactor} percent ${this.volumePercent} volume ${this.track.volume()}`);
	}

	public GetVolume(): number
	{
		return this.volumeFactor;
	}

	public SetVolumeAnalog(percent: number): void
	{
		if (this.track == null) return;

		this.volumePercent = percent;
		this.volume = this.volumeFactor * this.volumePercent;

		this.track.volume(this.volume);
		// console.log(`AudioManager.SetVolumeAnalog() ${this.Name} factor ${this.volumeFactor} percent ${this.volumePercent} volume ${this.track.volume()}`);
	}

	public GetVolumeAnalog(): number
	{
		return this.volumePercent;

		if (this.track == null) return 0;
		return this.track.volume();
	}

	public Load(): void {
		if (this.track == null) return;
		this.track.load();
	}

	public Duration(): number {
		if (this.track == null) return 0;
		return this.track.duration();
	}

	private loadedCallbacks: TrackLoadedCallback[] = [];
	public AddLoadedCallback(callback: TrackLoadedCallback) {
		this.loadedCallbacks.push(callback);
	}

	private loadErrorCallbacks: TrackLoadErrorCallback[] = [];
	public AddLoadErrorCallback(callback: TrackLoadErrorCallback) {
		this.loadErrorCallbacks.push(callback);
	}

	private OnLoaded(): void {
		console.log("Track " + this.name + " loaded");
		for (let i = 0; i < this.loadedCallbacks.length; i++) {
			this.loadedCallbacks[i]();
		}
	}

	private OnLoadError(): void {
		console.log("Track " + this.name + " loaded");
		for (let i = 0; i < this.loadErrorCallbacks.length; i++) {
			this.loadErrorCallbacks[i]();
		}
	}

	private OnEnd():void
	{
		if(this.loop && this.loopCount > 0)
		{
			this.loopCount --;
			if(this.loopCount == 0)
			{
				this.Stop();
			}
		}

	}

	get Name(): string {return this.name;}
	get Path(): string {return this.path;}
	get Required(): boolean {return this.required;}
}

export class DefaultTrack implements ITrack
{
	constructor() {
		this.Name = "default";
		this.Path = "";
		this.Required = false;
	}
	GetVolume(): number {
		return 0;
	}
	
	Pause(): void {
		//throw new Error("Method not implemented.");
	}

	Play(): void {
	}

	PlayLoop(loopCount: number): void {
	}

	PlayOrContinueLoop(loopCount: number): void {
	}

	Stop(): void {
	}

	Replay(): void {
	}
	IsPlaying(): boolean {
		return false;
	}

	SetVolume(volumeFactor: number): void {
	}

	Load(): void {
	}

	Duration(): number {
		return 0;
	}

	AddLoadedCallback(callback: TrackLoadedCallback): void {
	}

	AddLoadErrorCallback(callback: TrackLoadErrorCallback): void {
	}

	SetVolumeAnalog(percent: number): void{		
	}

	GetVolumeAnalog(): number {
		return 0
	}
	
	Name: string;
	Path: string;
	Required: boolean;

	
}

export class AudioManager
{
	protected tracks: Map<string, ITrack> = new Map<string, ITrack>();
	protected logGroup: string = "AudioManager";

	//protected parent: string = null;
	protected resid: ResourceIdentifier = null;
	protected defaultTrack: ITrack = new DefaultTrack

	protected volumeLevel: number = 0;
	public static readonly MaxVolumeLevel: number = 3;

	protected maxVolumeFactor: number = 0.0;


	public constructor(resid: ResourceIdentifier) {
		this.resid = resid.Add("Audio");

		(window as any).audioManager = this;
	}


	public async AllowAdd(cb: () => Promise<void>)
	{
		this.allowAdd = true;
		await cb();
		this.allowAdd = false;
	}

	public allowAdd: boolean = false;
	public AddTrack(track: Track) {
		if (!this.allowAdd) {
			throw `Adding track ${track.Name} not allowed`;
		}
		this.tracks.set(track.Name, track);
	}

	public Play(trackName: string, canPlayDuplicate?: boolean) {
		let t: ITrack = this.tracks.get(trackName);
		if (t) {
			t.Play(canPlayDuplicate);
		}
		else {
			Log.Error(this.logGroup, "Can't play track " + trackName + ", does not exist");
		}
	}

	public Replay(trackName: string) {
		let t: ITrack = this.tracks.get(trackName);
		if (t) {
			t.Replay();
		}
		else {
			Log.Error(this.logGroup, "Can't replay track " + trackName + ", does not exist");
		}
	}

	public PlayLoop(trackName: string, loopCount:number = Track.LoopForEver) {
		let t: ITrack = this.tracks.get(trackName);
		if (t) {
			t.PlayLoop(loopCount);
		}
		else {
			Log.Error(this.logGroup, "Can't play track " + trackName + ", does not exist");
		}
	}

	public PlayOrContinueLoop(trackName: string, loopCount: number = Track.LoopForEver): void
	{
		let t: ITrack = this.tracks.get(trackName);
		if (t) {
			t.PlayOrContinueLoop(loopCount);
		}
		else {
			Log.Error(this.logGroup, "Can't play track " + trackName + ", does not exist");
		}
	}

	public Stop(trackName: string) {
		let t: ITrack = this.tracks.get(trackName);
		if (t) {
			t.Stop();
		}
		else {
			Log.Error(this.logGroup, "Can't stop track " + trackName + ", does not exist");
		}
	}

	public Pause(trackName: string) {
		let t: ITrack = this.tracks.get(trackName);
		if (t) {
			t.Pause();
		}
		else {
			Log.Error(this.logGroup, "Can't stop track " + trackName + ", does not exist");
		}
	}

	public StopAll(): void {
		this.tracks.forEach((track: ITrack, name: string) => {
			if (track.IsPlaying()) track.Stop();
		});
	}

	private SetVolumeFactor(trackName: string, volumeFactor: number) {
		let t: ITrack = this.tracks.get(trackName);
		if (t) {
			if (volumeFactor < 0) {
				Log.Error(this.logGroup, "Not valid volume factor " + volumeFactor + ", must be in range [0..1]")
			}
			else if (volumeFactor > 1) {
				Log.Error(this.logGroup, "Not valid volume factor " + volumeFactor + ", must be in range [0..1]")
			}
			else {
				t.SetVolume(volumeFactor);
			}
		}
		else {
			Log.Error(this.logGroup, "Can't set volume to track " + trackName + ", does not exist");
		}
	}

	public Track(trackName: string): ITrack {
		let t: ITrack = this.tracks.get(trackName);
		if (t) {
			return t
		}
		else {
			Log.Error(this.logGroup, "Track " + trackName + ", does not exist");
			return this.defaultTrack;
		}
		//return this.defaultTrack;
	}

	private SetVolumeFactorAll(volumeFactor: number) {
		if (volumeFactor < 0) {
			Log.Error(this.logGroup, "Not valid volume factor " + volumeFactor + ", must be in range [0..1]")
		}
		else if (volumeFactor > 1) {
			Log.Error(this.logGroup, "Not valid volume factor " + volumeFactor + ", must be in range [0..1]")
		}
		else {
			let scaledVolumeFactor = volumeFactor * this.maxVolumeFactor;
			// Log.Debug(this.logGroup, "Setting volume factor " + volumeFactor + " scaled with " + this.maxVolumeFactor + " to " + scaledVolumeFactor);

			this.tracks.forEach((t: ITrack) => t.SetVolume(scaledVolumeFactor));
		}
	}

	// public GetResource(trackName: string) {
	// 	let track: ITrack = this.tracks.get(trackName);
	// 	if (track) {
	// 		// TODO make track hold its own resid
	// 		let trackResid: ResourceIdentifier = this.resid.Add(track.Name);
	// 		return new TrackResource(track, trackResid.Scope(), track.Required);
	// 	}
	// 	else {
	// 	}

	// 	return null;
	// }

	protected lastNumberOfResources: number = 0;
	public GetNewResources(): Resource[]
	{
		let newResources = this.GetResources(this.lastNumberOfResources);
		this.lastNumberOfResources += newResources.length;

		return newResources;
	}

	public GetResources(skip: number = 0): Resource[] {

		let resources: TrackResource[] = [...this.tracks.entries()]
			.slice(skip)
			.map(([name, track]) => new TrackResource(track, this.resid.Add(track.Name).Scope(), track.Required));

		return resources;
	}

	private volumeChangeCalbacks: VolumeChangeCallback[] = [];
	public OnVolumeChange(callback: VolumeChangeCallback): void {
		this.volumeChangeCalbacks.push(callback);
	}

	private VolumeChange(volumeLevel: number, volumeFactor: number) {
		for (let i: number = 0; i < this.volumeChangeCalbacks.length; i++) {
			this.volumeChangeCalbacks[i](volumeLevel, volumeFactor);
		}
	}

	public NextVolume():void{

		if(this.volumeLevel == AudioManager.MaxVolumeLevel)
		{
			this.volumeLevel=0;
		}
		else
		{
			this.volumeLevel++;
		}

		let factor: number = this.volumeLevel / AudioManager.MaxVolumeLevel;
		this.SetVolumeFactorAll(factor);

		this.VolumeChange(this.volumeLevel, factor);
	}

	public SetVolume(volumeLevel : number)
	{
		this.volumeLevel = volumeLevel;

		let factor: number = this.volumeLevel / AudioManager.MaxVolumeLevel;
		this.SetVolumeFactorAll(factor);
		this.VolumeChange(this.volumeLevel, factor);
	}


	protected maxVolumeFactorCallbacks: MaxVolumeFactorChangeChallback[] = [];
	public OnMaxVolumeFactorChange(callback: MaxVolumeFactorChangeChallback): void
	{
		this.maxVolumeFactorCallbacks.push(callback);
	}

	protected MaxVolumeFactorChanged(): void
	{
		for (let i = 0; i < this.maxVolumeFactorCallbacks.length; i++)
		{
			this.maxVolumeFactorCallbacks[i](this.maxVolumeFactor);
		}
	}

	public SetMaxVolumeFactor(maxVolumeFactor: number)
	{
		if (maxVolumeFactor >= 0 && maxVolumeFactor <= 1)
		{
			// Log.Debug(this.logGroup, "Setting max volume factor to " + maxVolumeFactor);
			this.maxVolumeFactor = maxVolumeFactor;

			let factor: number = this.volumeLevel / AudioManager.MaxVolumeLevel;
			this.SetVolumeFactorAll(factor);

			this.MaxVolumeFactorChanged();
		}
		else
		{
			Log.Error(this.logGroup, "Error settings max volume factor to " + maxVolumeFactor);
			// error settings volume factor
		}
	}

	public get MaxVolumeFactor(): number
	{
		return this.maxVolumeFactor;
	}

	public ListPlaying(): void
	{
		// Log.Debug(this.logGroup, "Listing playing tracks...");
		this.tracks.forEach((track) => {
			if (track.IsPlaying())
			{
				Log.Debug(this.logGroup, "Track is playing: " + track.Name);
			}
		});
		// Log.Debug(this.logGroup, "Listing finished");

	}

	public GetAudioLevel(): number {
		//console.log("AuidioLevelMachine:", this.volumeLevel / AudioManager.MaxVolumeLevel)
		return this.volumeLevel / AudioManager.MaxVolumeLevel
	}
	public GetVolumeLevel():number
	{
		return this.volumeLevel;
	}
}